import { Component, computed, inject, OnInit, signal, ViewChild, ElementRef, AfterViewInit, OnDestroy, effect } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { SidebarService } from '@shared/services/sidebar.service';
import { AuthService } from '@shared/services/auth.service';
import { BillingStateService } from '@shared/services/billing-state.service';
import { Router } from '@angular/router';
import { DataService } from '@shared/services/data.service';
import { User_request } from '../utils/interfaces';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'editor-sidebar',
  templateUrl: './editor-sidebar.component.html',
  styleUrls: ['./editor-sidebar.component.scss'],
  animations: [
    trigger('slide', [
      state('closed', style({
        transform: 'translateX(-100%)'
      })),
      state('open', style({
        transform: 'translateX(0)'
      })),
      transition('open <=> closed', animate('300ms ease-in-out'))
    ])
  ]
})
export class EditorSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly authService = inject(AuthService);
  public readonly sideBarService = inject(SidebarService);
  public readonly billingState = inject(BillingStateService);

  @ViewChild('requestsContainer') private requestsContainer!: ElementRef<HTMLDivElement>;
  private scrollSubscription: Subscription | undefined;


  readonly isOpenEditorSwitcher = signal<boolean>(false);
  readonly userImg = computed(() => {
    const user = this.authService.user();

    return user ? this.dataService.loadAvatar(user) : 'assets/images/default-avatar.png'
  })

  // Returns the custom avatar URL or null if not set
  readonly userCustomAvatarUrl = computed(() => {
    const user = this.authService.user();
    return user ? this.dataService.loadAvatar(user) : null;
  });

  // Generates user initials
  readonly userInitials = computed(() => {
    const user = this.authService.user();
    if (!user) {
      return '?'; // Fallback if no user
    }

    const name = user.name;
    const username = user.username;
    let initials = '';

    if (name && name.trim()) {
      const nameParts = name.trim().split(' ').filter(part => part.length > 0);
      if (nameParts.length >= 2) {
        // Use first letter of the first and last part of the name
        initials = (nameParts[0][0] + (nameParts[nameParts.length - 1][0] || '')).toUpperCase();
      } else if (nameParts.length === 1 && nameParts[0].length > 0) {
        // Use the first letter of the single name part
        initials = nameParts[0][0].toUpperCase();
      }
    }

    if (!initials && username && username.trim()) {
      // Fallback to the first letter of the username if name didn't yield initials
      initials = username.trim()[0].toUpperCase();
    }

    return initials || '?'; // Final fallback
  });

  mouseOver = false;

  // Sorted reactive list derived from the service signal
  readonly sortedRequests = computed<User_request[]>(() => {
    const list = this.sideBarService.requestsSignal();
    return (list || [])
      .slice()
      .sort((a, b) => new Date(b.updated || b.created || 0).getTime() - new Date(a.updated || a.created || 0).getTime());
  });

  getSlideBTNClass() {
    if (this.sideBarService.isSideBarCollapsed() && this.mouseOver) {
      return 'bi bi-chevron-compact-left';
    } else if (this.sideBarService.isSideBarCollapsed() && !this.mouseOver) {
      return 'bi bi-three-dots-vertical';
    } else {
      return 'bi bi-chevron-compact-right';
    }
  }

  constructor(private dataService: DataService, private router: Router) { }

  async onRequestClick(request: User_request) {
    // Ensure we have the latest data before navigating
    await this.sideBarService.updateLoadedRequest(request.id);
    // Set the active request ID for highlighting
    this.sideBarService.setActiveRequestId(request.id);
    // Get the freshly fetched request data to determine correct route
    const freshRequest = this.sideBarService.userRequest();
    const route = (freshRequest?.status === 88 || freshRequest?.status === 89 || freshRequest?.status === 90) ? 'result' : (freshRequest?.edit_route || 'editor');
    this.router.navigate(['/case-editor', route, request.id]);
  }

  logout() {
    this.authService.logout();
    this.billingState.clear();
    this.router.navigate(['/auth/login']);
  }

  openBilling(): void {
    this.router.navigate(['/account/settings'], {
      queryParams: { tab: 'billing' }
    });
  }

  async ngOnInit() {
    await this.sideBarService.updateRequests();

    // Restore scroll position after requests have been loaded and rendered
    this.restoreScrollPosition();
  }

  ngAfterViewInit(): void {
    // We use RxJS for a more robust scroll listener
    this.scrollSubscription = fromEvent(this.requestsContainer.nativeElement, 'scroll').pipe(
      debounceTime(150) // Only save scroll position when the user stops scrolling for 150ms
    ).subscribe(() => {
      this.saveScrollPosition();
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    this.scrollSubscription?.unsubscribe();
  }

  private saveScrollPosition(): void {
    sessionStorage.setItem('sidebarScrollPosition', this.requestsContainer.nativeElement.scrollTop.toString());
  }

  private restoreScrollPosition(): void {
    const savedPosition = sessionStorage.getItem('sidebarScrollPosition');
    if (savedPosition) {
      // Use a timeout to ensure the *ngFor has finished rendering
      setTimeout(() => {
        if (this.requestsContainer?.nativeElement) {
          this.requestsContainer.nativeElement.scrollTop = parseInt(savedPosition, 10);
        }
      }, 0);
    }
  }

  async deleteRequest(event: Event, requestId: string) {
    event.stopPropagation();
    if (confirm('Are you sure you want to delete this request?')) {
      try {
        await this.dataService.deleteRequest(requestId);
        await this.sideBarService.updateRequests();
        console.log('Request deleted successfully');
      } catch (error) {
        console.error('Failed to delete request:', error);
      }
    }
  }

  async renameCase(event: Event, request: User_request): Promise<void> {
    event.stopPropagation();

    const currentName = request.display_name?.trim() || request.case.name;
    const newName = prompt('Ändere den Namen für Deine Anfrage:', currentName);

    if (newName && newName.trim() && newName !== currentName) {
      try {
        const trimmed = newName.trim();
        const success = await this.dataService.updateRequestDisplayName(request.id, trimmed);
        if (success) {
          request.display_name = trimmed;
        } else {
          console.error('Failed to update request display name');
        }
      } catch (error) {
        console.error('Error updating request display name:', error);
      }
    }
  }

  async downloadCsvOverview(requestId: string): Promise<void> {
    const url = await this.dataService.loadGutachtenCSV(requestId);
    const a = document.createElement('a');
    a.href = url;                            // direct file URL
    a.download = `${requestId}_overview.csv`;    // set the name
    a.target = '_blank';                       // ensure navigation
    document.body.appendChild(a);                 // (optional) some browsers need it in the DOM
    a.click();
    a.remove();
  }

}
